import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Progressbar } from "../Progressbar/Progressbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquare, faSquareCheck } from "@fortawesome/free-solid-svg-icons";
import "./Onboarding-2.css";
import { useOnboarding } from "../contexts/OnboardingContext";
import { toast } from "react-toastify";

export const Onboarding2 = () => {
  const navigate = useNavigate();
  const { onboardingData, setOnboardingData } = useOnboarding();
  const [step, setStep] = useState(1);
  const totalSteps = 4;

  const [selectedTasks, setSelectedTasks] = useState({});
  const [customText, setCustomText] = useState("");
  const [isCustomTextSelected, setIsCustomTextSelected] = useState(false);

  // Define the list of tasks (goals/focus areas) the user can select
  const tasks = [
    { id: "task1", label: "🧘 Peace of mind", value: "Peace of mind" },
    {
      id: "task2",
      label: "📈 Career & performance",
      value: "Career & performance",
    },
    {
      id: "task3",
      label: "💙 Emotional well-being",
      value: "Emotional well-being",
    },
    { id: "task4", label: "🌙 Sleep quality", value: "Sleep quality" },
    { id: "task5", label: "⚡ Stress levels", value: "Stress levels" },
    {
      id: "task6",
      label: "🤝 Workplace relationships",
      value: "Workplace relationships",
    },
    {
      id: "task7",
      label: "🔥 Motivation & energy",
      value: "Motivation & energy",
    },
    {
      id: "task8",
      label: "✍️ Write something else",
      value: "Write something else",
    },
  ];

  // Toggle checkbox selection
  const handleCheckboxChange = (taskId) => {
    if (taskId === "task8") {
      const nextState = !isCustomTextSelected;
      setIsCustomTextSelected(nextState);
      if (!nextState) {
        setCustomText("");
      }
    } else {
      setSelectedTasks((prevSelected) => ({
        ...prevSelected,
        [taskId]: !prevSelected[taskId],
      }));
    }
  };

  const handleCustomTextChange = (event) => {
    setCustomText(event.target.value);
  };

  // Handle next button click
  const goToNextPage = async () => {
    const focusAreas = Object.entries(selectedTasks)
      .filter(([_, isSelected]) => isSelected)
      .map(([id]) => tasks.find((task) => task.id === id)?.value)
      .filter(Boolean);

    if (isCustomTextSelected && customText.trim() !== "") {
      focusAreas.push(customText.trim());
    }

    if (isCustomTextSelected && customText.trim() === "") {
      toast.warn("Please write something in the custom field or unselect it.");
      return;
    }

    if (focusAreas.length === 0) {
      toast.warn("Please select at least one focus area before continuing.");
      return;
    }

    setOnboardingData({
      ...onboardingData,
      focus: focusAreas.join(", "),
    });

    if (step < totalSteps) {
      setStep(step + 1);
      navigate("/Onboarding-Header1");
    }
  };

  return (
    <div className="onboarding2-page-wrapper">
      {/* Top Navigation / Glassmorphic Back button */}
      <button
        type="button"
        className="onboarding2-back-btn"
        onClick={() => navigate(-1)}
        aria-label="Go Back"
      >
        <span className="onboarding2-back-arrow" aria-hidden="true">
          &larr;
        </span>
        <span className="onboarding2-back-label">Back</span>
      </button>

      {/* Main Content Container (Centered horizontally & vertically) */}
      <div className="onboarding2-main-card">
        {/* Progress Bar Container */}
        <div className="onboarding2-progress-wrapper">
          <Progressbar step={step} totalSteps={totalSteps} />
        </div>

        {/* Title Header */}
        <div className="onboarding2-heading-group">
          <h1 className="onboarding2-page-title">
            What do you most hope to improve with this app?
          </h1>
          <p className="onboarding2-page-subtitle">
            Select all the focus areas that matter to you.
          </p>
        </div>

        {/* Checklist Options */}
        <div className="onboarding2-checklist">
          {tasks.map((task) => {
            const isSelected =
              task.id === "task8"
                ? isCustomTextSelected
                : !!selectedTasks[task.id];

            return (
              <div
                key={task.id}
                className={`onboarding2-card ${isSelected ? "selected" : ""}`}
                onClick={() => handleCheckboxChange(task.id)}
                role="checkbox"
                aria-checked={isSelected}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    handleCheckboxChange(task.id);
                  }
                }}
              >
                <span className="onboarding2-card-label">{task.label}</span>
                <FontAwesomeIcon
                  className={`onboarding2-check-icon ${
                    isSelected ? "icon-selected" : "icon-unselected"
                  }`}
                  icon={isSelected ? faSquareCheck : faSquare}
                />
              </div>
            );
          })}

          {/* Expandable Custom Focus Text Area */}
          {isCustomTextSelected && (
            <textarea
              value={customText}
              onChange={handleCustomTextChange}
              placeholder="Tell us what you'd like to improve..."
              className="onboarding2-custom-textarea"
              rows={3}
              autoFocus
            />
          )}
        </div>

        {/* Continue Action Button */}
        <button
          type="button"
          className="onboarding2-continue-btn"
          onClick={goToNextPage}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default Onboarding2;
